import { calculateAgeFromBirthDate, zipCodeToState } from '@shared';
import {
  AdvisorInfoWithWritingCode,
  checkForSpecificConditions,
  checkIfEnrolledInMedicaid,
  checkMedicalApplianceUsage,
  convertHeightToFeetAndInches,
  formatDateForForm,
  GetAdvisorAddress,
  getClientWeightChange,
  getSubstanceTypes,
  mapGender,
  mapMaritalStatus,
  returnSafeString,
  tobaccoUseOptions,
} from './carrier-utils';

export const LINCOLN_PREFILL_SECTIONS_PRIMARY = [
  'proposed-insured',
  'existing-pending-insurance',
  'medical-and-long-term-care',
  'medical-ltc-supplemental',
  'interview-worksheet',
  'long-term-care-personal-worksheet',
];

export const getClientUpdatesForLincoln = (clientInfo: Client): PreFillUpdate[] => {
  const clientUpdates = [
    {
      questionRef: 'proposedInsuredFirst',
      answer: clientInfo.clientFirstName,
    },
    {
      questionRef: 'proposedInsuredLast',
      answer: clientInfo.clientLastName,
    },
    {
      questionRef: 'email',
      answer: clientInfo.clientEmail,
      sourceQuestionRefs: ['clientEmail'],
    },
  ].filter(update => update.answer);

  const intakeSurveyMappings = clientInfo.intakeSurvey ? getLincolnIntakeMappings(clientInfo) : [];

  const introIntakeSurveyMappings = clientInfo.introIntakeSurvey
    ? [
        {
          questionRef: 'primaryPhone',
          answer: clientInfo.introIntakeSurvey.clientPhoneNumber || '',
          sourceQuestionRefs: ['clientPhoneNumber'],
        },
        {
          questionRef: 'cellPhone',
          answer: clientInfo.introIntakeSurvey.clientPhoneNumber || '',
          sourceQuestionRefs: ['clientPhoneNumber'],
        },
      ].filter(update => update.answer)
    : [];

  return [...clientUpdates, ...intakeSurveyMappings, ...introIntakeSurveyMappings];
};

const getLincolnIntakeMappings = (clientInfo: Client): PreFillUpdate[] => {
  if (!clientInfo?.intakeSurvey) return [];

  const { intakeSurvey } = clientInfo;
  const heightData = convertHeightToFeetAndInches(intakeSurvey.clientHeightInches);
  const weightChangeData = getClientWeightChange(
    intakeSurvey.clientWeightGainedLost,
    intakeSurvey.clientWeightGainedLastTwelveMonths,
    intakeSurvey.clientWeightLostLastTwelveMonths,
  );
  const clientAge = intakeSurvey.clientBirthDate
    ? calculateAgeFromBirthDate(intakeSurvey.clientBirthDate)
    : undefined;

  const basicMappings = [
    {
      questionRef: 'proposedInsuredDob',
      answer: formatDateForForm(intakeSurvey.clientBirthDate),
      sourceQuestionRefs: ['clientBirthDate'],
    },
    {
      questionRef: 'proposedInsuredSex',
      answer: mapGender(intakeSurvey.clientGenderAtBirth || ''),
      sourceQuestionRefs: ['clientGenderAtBirth'],
    },
    {
      questionRef: 'married',
      answer: mapMaritalStatus(intakeSurvey.clientMaritalStatus || ''),
      sourceQuestionRefs: ['clientMaritalStatus'],
    },
    {
      questionRef: 'homeZip',
      answer: intakeSurvey.clientZipCode || '',
      sourceQuestionRefs: ['clientZipCode'],
    },
    {
      questionRef: 'homeState',
      answer: zipCodeToState(intakeSurvey?.clientZipCode).stateAbbreviation || '',
      sourceQuestionRefs: ['clientZipCode'],
    },
    {
      questionRef: 'coveredByMedicaid',
      answer: checkIfEnrolledInMedicaid(intakeSurvey.clientHealthPlanList),
      sourceQuestionRefs: ['clientHealthPlanList'],
    },
    {
      questionRef: 'medicalHeightFeet',
      answer: returnSafeString(heightData.feet),
      sourceQuestionRefs: ['clientHeightInches'],
    },
    {
      questionRef: 'medicalHeightInches',
      answer: returnSafeString(heightData.inches),
      sourceQuestionRefs: ['clientHeightInches'],
    },
    {
      questionRef: 'medicalWeight',
      answer: returnSafeString(intakeSurvey.clientWeightPounds),
      sourceQuestionRefs: ['clientWeightPounds'],
    },
    {
      questionRef: 'medicalWeightChangePast12Months',
      answer: weightChangeData.weightGained === undefined ? 'no' : 'yes',
      sourceQuestionRefs: ['clientWeightGainedLost'],
    },
    {
      questionRef: 'interviewHeight',
      answer: heightData.feet > 0 ? `${heightData.feet}' ${heightData.inches}"` : '',
      sourceQuestionRefs: ['clientHeightInches'],
    },
    {
      questionRef: 'interviewWeight',
      answer:
        (intakeSurvey.clientWeightPounds || 0) > 0
          ? intakeSurvey.clientWeightPounds.toString()
          : '',
      sourceQuestionRefs: ['clientWeightPounds'],
    },
    {
      questionRef: 'ltcAssetsWorth',
      answer:
        typeof intakeSurvey.householdTotalAssetsValue === 'number'
          ? intakeSurvey.householdTotalAssetsValue < 50000
            ? 'lt50k'
            : intakeSurvey.householdTotalAssetsValue <= 100000
              ? '50k-100k'
              : intakeSurvey.householdTotalAssetsValue <= 150000
                ? '100k-150k'
                : intakeSurvey.householdTotalAssetsValue > 150000
                  ? 'gt150k'
                  : ''
          : '',
      sourceQuestionRefs: ['householdTotalAssetsValue'],
    },
    {
      questionRef: 'ltcHouseholdIncome',
      answer:
        typeof intakeSurvey.householdPriorYearIncome === 'number'
          ? intakeSurvey.householdPriorYearIncome < 10000
            ? 'under-10000'
            : intakeSurvey.householdPriorYearIncome < 20000
              ? '10000-19999'
              : intakeSurvey.householdPriorYearIncome < 30000
                ? '20000-29999'
                : intakeSurvey.householdPriorYearIncome <= 50000
                  ? '30000-50000'
                  : intakeSurvey.householdPriorYearIncome > 50000
                    ? 'over-50000'
                    : ''
          : '',
      sourceQuestionRefs: ['householdPriorYearIncome'],
    },
    {
      questionRef: 'disclosureProposedInsuredAges',
      answer: clientAge ? returnSafeString(clientAge) : '',
      sourceQuestionRefs: ['clientBirthDate'],
    },
  ];

  const medicalMappings = getLincolnMedicalMappings(intakeSurvey);

  return [...basicMappings, ...medicalMappings].filter(update => update.answer);
};

const getLincolnMedicalMappings = (intakeSurvey: IntakeSurvey): PreFillUpdate[] => {
  const mappings = [
    // Medical condition yes/no mappings
    {
      questionRef: 'medicalAlcoholUse',
      answer: checkForSpecificConditions(intakeSurvey.clientSubstancesUsed, ['alcohol']),
      sourceQuestionRefs: ['clientSubstancesUsed'],
    },
    {
      questionRef: 'medicalADLAssistancePast5Years',
      answer: intakeSurvey.clientAdlReceivedAssistanceCount > 0 ? 'yes' : 'no',
      sourceQuestionRefs: ['clientAdlReceivedAssistanceList', 'clientDifficultyWithTasks'],
    },
    {
      questionRef: 'medicalDisabilityBenefitsPast12Months',
      answer: intakeSurvey?.clientDisabilityWorkersComp?.toLowerCase() === 'yes' ? 'yes' : 'no',
      sourceQuestionRefs: ['clientDisabilityWorkersComp'],
    },
    {
      questionRef: 'medicalCardiacCoronaryDisease',
      answer: checkForSpecificConditions(intakeSurvey.clientHeartConditionDiagnosis, [
        'cardiovascular',
        'heart',
        'angina',
        'chest pain',
        'heart attack',
        'Coronary Artery Disease',
        'CAD',
        'Congestive Heart Failure',
        'Cardiomyopathy or congestive heart failure (CHF)',
      ]),
      sourceQuestionRefs: ['clientHeartConditionDiagnosis'],
    },
    {
      questionRef: 'medicalOtherCardiacConditionsYesNo',
      answer: checkForSpecificConditions(intakeSurvey.clientHeartConditionDiagnosis, [
        'heart disease',
        'heart disorder',
        'Atrial fibrillation or other arrhythmia',
        'Peripheral vascular disease / aneurysm',
        'heart murmur',
        'Valvular Heart Disease',
        'Heart valve disease / murmur',
        'high blood pressure',
        'hypertension',
        'irregular heartbeat',
        'arrhythmia',
        'Atrial fibrillation or other arrhythmia',
        'peripheral vascular disease',
        'peripheral artery disease',
        'PAD',
        'Peripheral vascular disease / aneurysm',
        'high blood pressure',
      ]),
      sourceQuestionRefs: ['clientHeartConditionDiagnosis'],
    },
    {
      questionRef: 'medicalOtherCardiacConditions',
      answer: getCardiacConditions(intakeSurvey),
      sourceQuestionRefs: ['clientHeartConditionDiagnosis'],
    },
    {
      questionRef: 'medicalMentalHealthConditionsYesNo',
      answer:
        (intakeSurvey.clientMentalHealthIssuesList &&
          checkForSpecificConditions(intakeSurvey.clientMentalHealthIssuesList, [
            'anxiety',
            'depression',
            'bipolar disorder',
            'schizophrenia',
          ]) === 'yes') ||
        (intakeSurvey.clientPsychiatricDiagnosis &&
          intakeSurvey.clientPsychiatricDiagnosis.length > 0)
          ? 'yes'
          : 'no',
      sourceQuestionRefs: ['clientMentalHealthIssuesList', 'clientPsychiatricDiagnosis'],
    },
    {
      questionRef: 'medicalMentalHealthConditions',
      answer: getMentalHealthConditions(intakeSurvey),
      sourceQuestionRefs: ['clientMentalHealthIssuesList', 'clientPsychiatricDiagnosis'],
    },
    {
      questionRef: 'medicalCancerConditionsYesNo',
      answer:
        checkForSpecificConditions(intakeSurvey.clientDiagnosesList, ['cancer']) === 'yes' ||
        (intakeSurvey.clientCancerTypeLocation && intakeSurvey.clientCancerTypeLocation.length > 0)
          ? 'yes'
          : 'no',
      sourceQuestionRefs: ['clientCancerTypeLocation'],
    },
    {
      questionRef: 'medicalCancerConditions',
      answer: getCancerConditions(intakeSurvey),
      sourceQuestionRefs: ['clientCancerTypeLocation'],
    },
    {
      questionRef: 'medicalMetabolicConditionsYesNo',
      answer: checkForSpecificConditions(intakeSurvey.clientDiagnosesList, ['diabetes']),
      sourceQuestionRefs: ['clientDiagnosesList'],
    },
    {
      questionRef: 'medicalMetabolicConditions',
      answer: getMetabolicConditions(intakeSurvey),
      sourceQuestionRefs: ['clientEENTDiagnosis', 'clientDigestiveDiagnosis'],
    },
    {
      questionRef: 'medicalRespiratoryConditionsYesNo',
      answer: checkForSpecificConditions(intakeSurvey.clientDiagnosesList, [
        'respiratory',
        'lung',
        'COPD',
        'asthma',
      ]),
      sourceQuestionRefs: ['clientLungRespiratoryDiagnosis'],
    },
    {
      questionRef: 'medicalRespiratoryConditions',
      answer: getRespiratoryConditions(intakeSurvey),
      sourceQuestionRefs: ['clientLungRespiratoryDiagnosis'],
    },
    {
      questionRef: 'medicalGastrointestinalConditionsYesNo',
      answer:
        (intakeSurvey.clientDigestiveDiagnosis &&
          intakeSurvey.clientDigestiveDiagnosis.length > 0) ||
        checkForSpecificConditions(intakeSurvey.clientDiagnosesList, [
          'digestive',
          'gastrointestinal',
          'GERD',
          'hepatitis',
          'pancreatitis',
          "crohn's",
          'colitis',
          'IBS',
        ]) === 'yes'
          ? 'yes'
          : 'no',
      sourceQuestionRefs: ['clientDigestiveDiagnosis'],
    },
    {
      questionRef: 'medicalGastrointestinalConditions',
      answer: getGastrointestinalConditions(intakeSurvey),
      sourceQuestionRefs: ['clientDigestiveDiagnosis'],
    },
    {
      questionRef: 'medicalUrinaryConditionsYesNo',
      answer:
        checkForSpecificConditions(intakeSurvey.clientDiagnosesList, [
          'kidney',
          'urinary',
          'bladder',
          'renal',
        ]) === 'yes' ||
        (intakeSurvey.clientGenitourinaryDiagnosis &&
          intakeSurvey.clientGenitourinaryDiagnosis.length > 0)
          ? 'yes'
          : 'no',
      sourceQuestionRefs: ['clientGenitourinaryDiagnosis'],
    },
    {
      questionRef: 'medicalUrinaryConditions',
      answer: getUrinaryConditions(intakeSurvey),
      sourceQuestionRefs: ['clientGenitourinaryDiagnosis'],
    },
    {
      questionRef: 'medicalBloodDisordersYesNo',
      answer: checkForSpecificConditions(intakeSurvey.clientDiagnosesList, [
        'blood disorder',
        'anemia',
        'clotting disorder',
        'DVT',
        'deep vein thrombosis',
        'pulmonary embolism',
        'thrombocytopenia',
        'hemochromatosis',
      ]),
      sourceQuestionRefs: ['clientDiagnosesList'],
    },
    {
      questionRef: 'medicalBloodDisorders',
      answer: getBloodDisorders(intakeSurvey),
      sourceQuestionRefs: ['clientDiagnosesList'],
    },
    {
      questionRef: 'medicalMusculoskeletalConditionsYesNo',
      answer: checkForSpecificConditions(intakeSurvey.clientDiagnosesList, [
        'arthritis',
        'musculoskeletal',
        'osteoporosis',
        'fibromyalgia',
        'gout',
        'fracture',
        'sciatica',
        'scoliosis',
        'osteopenia',
        'chronic joint pain',
      ]),
      sourceQuestionRefs: ['clientInjuriesList'],
    },
    {
      questionRef: 'medicalMusculoskeletalConditions',
      answer: getMusculoskeletalConditions(intakeSurvey),
      sourceQuestionRefs: ['clientInjuriesList'],
    },
    {
      questionRef: 'medicalBrainNervousConditionsYesNo',
      answer:
        (intakeSurvey.clientBrainInjuryDiagnosis &&
          intakeSurvey.clientBrainInjuryDiagnosis.length > 0) ||
        checkForSpecificConditions(intakeSurvey.clientDiagnosesList, [
          'neurological',
          'brain',
          'nervous',
          'stroke',
          'TIA',
          'seizure',
          'epilepsy',
          'tremor',
          'neuropathy',
          'migraine',
          'concussion',
        ]) === 'yes'
          ? 'yes'
          : 'no',
      sourceQuestionRefs: ['clientBrainInjuryDiagnosis'],
    },
    {
      questionRef: 'medicalBrainNervousConditions',
      answer: getBrainNervousConditions(intakeSurvey),
      sourceQuestionRefs: ['clientBrainInjuryDiagnosis'],
    },
    {
      questionRef: 'medicalCannabisUsePast5Years',
      answer: checkForSpecificConditions(intakeSurvey.clientSubstancesUsed, [
        'Marijuana',
        'cannabis',
        'THC',
      ]),
      sourceQuestionRefs: ['clientSubstancesUsed'],
    },
    {
      questionRef: 'medicalHIVDiagnosis',
      answer: checkForSpecificConditions(intakeSurvey.clientImmuneSystemDiagnosis, [
        'HIV positive',
        'HIV',
      ]),
      sourceQuestionRefs: ['clientImmuneSystemDiagnosis'],
    },
    {
      questionRef: 'medicalHospitalPatientPast5Years',
      answer:
        checkForSpecificConditions(intakeSurvey.clientHospitalizedERLastTwelveMonths, [
          'Hospitalized',
          'Visited the ER',
        ]) === 'yes'
          ? 'yes'
          : intakeSurvey.clientPriorTwoYearHospitalizationCount > 0
            ? 'yes'
            : 'no',
      sourceQuestionRefs: [
        'clientHospitalizedERLastTwelveMonths',
        'clientPriorTwoYearHospitalizationCount',
      ],
    },
    {
      questionRef: 'medicalIllegalDrugUsePast10Years',
      answer: checkForSpecificConditions(intakeSurvey.clientSubstancesUsed, [
        'cocaine',
        'hallucinogens',
        'narcotics',
        'heroin',
        'methamphetamine',
        'Mushrooms',
        'hard drugs',
      ]),
      sourceQuestionRefs: ['clientSubstancesUsed'],
    },
    // Smoking and alcohol
    {
      questionRef: 'suppSmokedCigarettesPast12Months',
      answer:
        intakeSurvey.clientSmokerEver === 'Yes' || intakeSurvey.clientSmokedRecently === 'Yes'
          ? 'yes'
          : 'no',
      sourceQuestionRefs: ['clientSmokerEver', 'clientSmokedRecently'],
    },
    {
      questionRef: 'medicalAlcoholFrequency',
      answer:
        typeof intakeSurvey.clientAlcoholLastFiveYears === 'number'
          ? returnSafeString(intakeSurvey.clientAlcoholLastFiveYears / 4)
          : '',
      sourceQuestionRefs: ['clientAlcoholLastFiveYears'],
    },
    // Interview Worksheet - Disability Benefits (radio)
    {
      questionRef: 'interviewDisabilityBenefits',
      answer: intakeSurvey.clientDisabilityWorkersComp?.toLowerCase() === 'yes' ? 'yes' : 'no',
      sourceQuestionRefs: ['clientDisabilityWorkersComp'],
    },
    // Interview Worksheet - Handicap Permit (radio)
    {
      questionRef: 'interviewHandicapPermit',
      answer:
        intakeSurvey.clientDifficultyWithTasks?.length > 0 ||
        intakeSurvey.clientAdlReceivedAssistanceCount > 0
          ? 'yes'
          : 'no',
      sourceQuestionRefs: ['clientDifficultyWithTasks', 'clientAdlReceivedAssistanceList'],
    },
    // Interview Worksheet - Tobacco/Alcohol History (table)
    {
      questionRef: 'interviewTobaccoAlcoholHistory',
      answer: [
        {
          tobaccoUse:
            checkForSpecificConditions(intakeSurvey.clientSubstancesUsed, [
              'Tobacco / cigarettes',
              'Vaping',
              'Cigars',
              'Chewing tobacco / snuff',
            ]) === 'yes' || intakeSurvey.clientSmokerEver?.toLowerCase() === 'yes'
              ? getSubstanceTypes(intakeSurvey.clientSubstancesUsed, tobaccoUseOptions)
              : 'None',
          alcoholUse:
            typeof intakeSurvey.clientAlcoholLastFiveYears === 'number'
              ? returnSafeString(intakeSurvey.clientAlcoholLastFiveYears / 4)
              : 'None',
        },
      ],
      sourceQuestionRefs: [
        'clientSubstancesUsed',
        'clientSmokerEver',
        'clientAlcoholLastFiveYears',
      ],
    },
    // Interview Worksheet - Blood Pressure (table)
    {
      questionRef: 'interviewBloodPressure',
      answer: intakeSurvey.clientBloodPressureReading
        ? [
            {
              reading: intakeSurvey.clientBloodPressureReading,
            },
          ]
        : [],
      sourceQuestionRefs: ['clientBloodPressureReadings'],
    },
    // Disability benefits
    {
      questionRef: 'suppSSDI',
      answer: intakeSurvey.clientDisabilityWorkersComp?.toLowerCase() === 'yes' ? 'yes' : 'no',
      sourceQuestionRefs: ['clientDisabilityWorkersComp'],
    },
    // Assistive devices and care
    {
      questionRef: 'suppUsesCane',
      answer: checkMedicalApplianceUsage(intakeSurvey.clientAssistiveDevicesList) ? 'yes' : 'no',
      sourceQuestionRefs: ['clientAssistiveDevicesList'],
    },
    {
      questionRef: 'suppPhysicalTherapyCurrent',
      answer: checkForSpecificConditions(intakeSurvey.clientTherapyTypes, ['physical therapy']),
      sourceQuestionRefs: ['clientTherapyTypes'],
    },
    // Supplement Section - Major Conditions
    {
      questionRef: 'suppAlzheimersDementia',
      answer: checkForSpecificConditions(intakeSurvey.clientCognitiveMemoryDiagnosis, [
        "Alzheimer's",
        'Dementia',
        'Memory loss',
      ]),
      sourceQuestionRefs: ['clientCognitiveMemoryDiagnosis'],
    },
    {
      questionRef: 'suppAls',
      answer: checkForSpecificConditions(intakeSurvey.clientDiagnosesList, ['ALS', 'Lou Gehrig']),
      sourceQuestionRefs: ['clientDiagnosesList'],
    },
    {
      questionRef: 'suppChronicKidneyDisease',
      answer: checkForSpecificConditions(intakeSurvey.clientGenitourinaryDiagnosis, [
        'Chronic kidney disease',
        'chronic renal',
        'kidney failure',
      ]),
      sourceQuestionRefs: ['clientGenitourinaryDiagnosis'],
    },
    {
      questionRef: 'suppCirrhosis',
      answer: checkForSpecificConditions(intakeSurvey.clientDigestiveDiagnosis, ['cirrhosis']),
      sourceQuestionRefs: ['clientDigestiveDiagnosis'],
    },
    {
      questionRef: 'suppCongestiveHeartFailure',
      answer: checkForSpecificConditions(intakeSurvey.clientHeartConditionDiagnosis, [
        'Congestive Heart Failure',
        'CHF',
        'heart failure',
        'Cardiomyopathy or congestive heart failure (CHF)',
      ]),
      sourceQuestionRefs: ['clientHeartConditionDiagnosis'],
    },
    {
      questionRef: 'suppHemophilia',
      answer: checkForSpecificConditions(intakeSurvey.clientDiagnosesList, ['hemophilia']),
      sourceQuestionRefs: ['clientDiagnosesList'],
    },
    {
      questionRef: 'suppHuntingtonsDisease',
      answer: checkForSpecificConditions(intakeSurvey.clientDiagnosesList, ["huntington's"]),
      sourceQuestionRefs: ['clientDiagnosesList'],
    },
    {
      questionRef: 'suppImplantableDefibrillator',
      answer: checkForSpecificConditions(intakeSurvey.clientHeartConditionDiagnosis, [
        'ICD',
        'implantable defibrillator',
        'pacemaker',
      ]),
      sourceQuestionRefs: ['clientHeartConditionDiagnosis'],
    },
    {
      questionRef: 'suppLeukemia',
      answer: checkForSpecificConditions(intakeSurvey.clientCancerTypeLocation, ['Leukemia']),
      sourceQuestionRefs: ['clientCancerTypeLocation'],
    },
    {
      questionRef: 'suppMemoryLossWithMedication',
      answer: checkForSpecificConditions(intakeSurvey.clientCognitiveMemoryDiagnosis, [
        'Memory loss',
        "Alzheimer's",
        'Dementia',
      ]),
      sourceQuestionRefs: ['clientCognitiveMemoryDiagnosis'],
    },
    {
      questionRef: 'suppMultipleSclerosis',
      answer: checkForSpecificConditions(intakeSurvey.clientDiagnosesList, [
        'multiple sclerosis',
        'MS',
      ]),
      sourceQuestionRefs: ['clientDiagnosesList'],
    },
    {
      questionRef: 'suppMultipleStrokesTIA',
      answer: checkForSpecificConditions(intakeSurvey.clientBrainInjuryDiagnosis, [
        'Multiple strokes',
        'TIA',
        'stroke',
      ]),
      sourceQuestionRefs: ['clientBrainInjuryDiagnosis'],
    },
    {
      questionRef: 'suppMuscularDystrophy',
      answer: checkForSpecificConditions(intakeSurvey.clientDiagnosesList, ['muscular dystrophy']),
      sourceQuestionRefs: ['clientDiagnosesList'],
    },
    {
      questionRef: 'suppMyastheniaGravis',
      answer: checkForSpecificConditions(intakeSurvey.clientDiagnosesList, ['myasthenia gravis']),
      sourceQuestionRefs: ['clientDiagnosesList'],
    },
    {
      questionRef: 'suppOrganTransplantNonCornea',
      answer: checkForSpecificConditions(intakeSurvey.clientDiagnosesList, [
        'organ transplant',
        'transplant',
      ]),
      sourceQuestionRefs: ['clientDiagnosesList'],
    },
    {
      questionRef: 'suppParkinsonsDisease',
      answer: checkForSpecificConditions(intakeSurvey.clientBrainInjuryDiagnosis, [
        'Parkinson',
        'Parkinsonism',
      ]),
      sourceQuestionRefs: ['clientBrainInjuryDiagnosis'],
    },
    {
      questionRef: 'suppPostPolioSyndrome',
      answer: checkForSpecificConditions(intakeSurvey.clientDiagnosesList, ['post-polio', 'polio']),
      sourceQuestionRefs: ['clientDiagnosesList'],
    },
    {
      questionRef: 'suppProgressiveMacularDegeneration',
      answer: checkForSpecificConditions(intakeSurvey.clientEENTDiagnosis, [
        'Macular degeneration',
      ]),
      sourceQuestionRefs: ['clientEENTDiagnosis'],
    },
    {
      questionRef: 'suppSchizophrenia',
      answer: checkForSpecificConditions(intakeSurvey.clientPsychiatricDiagnosis, [
        'Schizophrenia',
      ]),
      sourceQuestionRefs: ['clientPsychiatricDiagnosis'],
    },
    {
      questionRef: 'suppScleroderma',
      answer: checkForSpecificConditions(intakeSurvey.clientImmuneSystemDiagnosis, ['Scleroderma']),
      sourceQuestionRefs: ['clientImmuneSystemDiagnosis'],
    },
    {
      questionRef: 'suppSystemicLupus',
      answer: checkForSpecificConditions(intakeSurvey.clientImmuneSystemDiagnosis, ['Lupus']),
      sourceQuestionRefs: ['clientImmuneSystemDiagnosis'],
    },
    {
      questionRef: 'suppType1Diabetes',
      answer: checkForSpecificConditions(intakeSurvey.clientDiabetesTypeDiagnosisList, ['Type 1']),
      sourceQuestionRefs: ['clientDiabetesTypeDiagnosisList'],
    },
    // Supplement Section - Assistive Devices
    {
      questionRef: 'suppUsesCatheter',
      answer: checkForSpecificConditions(intakeSurvey.clientAssistiveDevicesList, ['catheter']),
      sourceQuestionRefs: ['clientAssistiveDevicesList'],
    },
    {
      questionRef: 'suppUsesDialysisMachine',
      answer: checkForSpecificConditions(intakeSurvey.clientAssistiveDevicesList, ['dialysis']),
      sourceQuestionRefs: ['clientAssistiveDevicesList'],
    },
    {
      questionRef: 'suppHasHandicapParkingPermit',
      answer: checkForSpecificConditions(intakeSurvey.clientAssistiveDevicesList, [
        'handicap parking',
        'disabled parking',
      ]),
      sourceQuestionRefs: ['clientAssistiveDevicesList'],
    },
    {
      questionRef: 'suppUsesOxygen',
      answer: checkForSpecificConditions(intakeSurvey.clientAssistiveDevicesList, ['oxygen']),
      sourceQuestionRefs: ['clientAssistiveDevicesList'],
    },
    {
      questionRef: 'suppUsesWalker',
      answer: checkForSpecificConditions(intakeSurvey.clientAssistiveDevicesList, ['walker']),
      sourceQuestionRefs: ['clientAssistiveDevicesList'],
    },
    {
      questionRef: 'suppUsesWheelchair',
      answer: checkForSpecificConditions(intakeSurvey.clientAssistiveDevicesList, ['wheelchair']),
      sourceQuestionRefs: ['clientAssistiveDevicesList'],
    },
    // Supplement Section - ADL and SSDI (already mapped above)
    {
      questionRef: 'suppADLDisorder',
      answer: intakeSurvey.clientAdlReceivedAssistanceCount > 0 ? 'yes' : 'no',
      sourceQuestionRefs: ['clientAdlReceivedAssistanceList', 'clientDifficultyWithTasks'],
    },
    // Supplement Section - Osteoporosis Related
    {
      questionRef: 'suppOsteoporosis',
      answer: checkForSpecificConditions(intakeSurvey.clientDiagnosesList, ['osteoporosis']),
      sourceQuestionRefs: ['clientDiagnosesList'],
    },
    {
      questionRef: 'suppCompressionFracture',
      answer: checkForSpecificConditions(intakeSurvey.clientInjuriesList, [
        'compression fracture',
        'vertebral fracture',
      ]),
      sourceQuestionRefs: ['clientInjuriesList'],
    },
    {
      questionRef: 'suppKyphosis',
      answer: checkForSpecificConditions(intakeSurvey.clientDiagnosesList, ['kyphosis']),
      sourceQuestionRefs: ['clientInjuriesList'],
    },
    {
      questionRef: 'suppSpinalStenosis',
      answer: checkForSpecificConditions(intakeSurvey.clientInjuriesList, ['spinal stenosis']),
      sourceQuestionRefs: ['clientInjuriesList'],
    },
    {
      questionRef: 'suppSurgicallyTreatedScoliosis',
      answer: checkForSpecificConditions(intakeSurvey.clientInjuriesList, ['scoliosis surgery']),
      sourceQuestionRefs: ['clientInjuriesList'],
    },
    // Supplement Section - Smoking and Related Conditions
    {
      questionRef: 'suppAsthma',
      answer: checkForSpecificConditions(intakeSurvey.clientLungRespiratoryDiagnosis, ['Asthma']),
      sourceQuestionRefs: ['clientLungRespiratoryDiagnosis'],
    },
    {
      questionRef: 'suppCarotidArteryDisease',
      answer: checkForSpecificConditions(intakeSurvey.clientHeartConditionDiagnosis, [
        'carotid artery',
      ]),
      sourceQuestionRefs: ['clientHeartConditionDiagnosis'],
    },
    {
      questionRef: 'suppCOPDEmphysema',
      answer: checkForSpecificConditions(intakeSurvey.clientLungRespiratoryDiagnosis, [
        'COPD',
        'Emphysema',
        'COPD / emphysema',
      ]),
      sourceQuestionRefs: ['clientLungRespiratoryDiagnosis'],
    },
    {
      questionRef: 'suppCoronaryArteryDisease6',
      answer: checkForSpecificConditions(intakeSurvey.clientHeartConditionDiagnosis, [
        'Coronary Artery Disease',
        'CAD',
      ]),
      sourceQuestionRefs: ['clientHeartConditionDiagnosis'],
    },
    {
      questionRef: 'suppDiabetes6',
      answer: checkForSpecificConditions(intakeSurvey.clientDiagnosesList, ['diabetes']),
      sourceQuestionRefs: ['clientDiagnosesList'],
    },
    {
      questionRef: 'suppDiabetes10Years',
      answer: checkForSpecificConditions(intakeSurvey.clientDiagnosesList, ['diabetes']),
      sourceQuestionRefs: ['clientDiagnosesList'],
    },
    {
      questionRef: 'suppHeartAttack6',
      answer: checkForSpecificConditions(intakeSurvey.clientHeartConditionDiagnosis, [
        'heart attack',
      ]),
      sourceQuestionRefs: ['clientHeartConditionDiagnosis'],
    },
    {
      questionRef: 'suppPeripheralVascularDisease6',
      answer: checkForSpecificConditions(intakeSurvey.clientHeartConditionDiagnosis, [
        'Peripheral vascular disease',
        'PAD',
      ]),
      sourceQuestionRefs: ['clientHeartConditionDiagnosis'],
    },
    {
      questionRef: 'suppSarcoidosis6',
      answer: checkForSpecificConditions(intakeSurvey.clientLungRespiratoryDiagnosis, [
        'sarcoidosis',
      ]),
      sourceQuestionRefs: ['clientLungRespiratoryDiagnosis'],
    },
    {
      questionRef: 'suppSleepApnea6',
      answer: checkForSpecificConditions(intakeSurvey.clientLungRespiratoryDiagnosis, [
        'Sleep apnea',
      ]),
      sourceQuestionRefs: ['clientLungRespiratoryDiagnosis'],
    },
    {
      questionRef: 'suppStrokeTIA6',
      answer: checkForSpecificConditions(intakeSurvey.clientBrainInjuryDiagnosis, [
        'stroke',
        'TIA',
        'Single stroke',
        'Multiple strokes',
      ]),
      sourceQuestionRefs: ['clientBrainInjuryDiagnosis'],
    },
    // Supplement Section - Diabetes Complications
    {
      questionRef: 'suppAtrialFibrillation7',
      answer: checkForSpecificConditions(intakeSurvey.clientHeartConditionDiagnosis, [
        'Atrial fibrillation',
        'afib',
        'a-fib',
      ]),
      sourceQuestionRefs: ['clientHeartConditionDiagnosis'],
    },
    {
      questionRef: 'suppCirculatoryDisorder7',
      answer: checkForSpecificConditions(intakeSurvey.clientHeartConditionDiagnosis, [
        'circulatory',
        'peripheral vascular',
      ]),
      sourceQuestionRefs: ['clientHeartConditionDiagnosis'],
    },
    {
      questionRef: 'suppCoronaryArteryDisease7',
      answer: checkForSpecificConditions(intakeSurvey.clientHeartConditionDiagnosis, [
        'Coronary Artery Disease',
        'CAD',
      ]),
      sourceQuestionRefs: ['clientHeartConditionDiagnosis'],
    },
    {
      questionRef: 'suppNeuropathy7',
      answer: checkForSpecificConditions(intakeSurvey.clientBrainInjuryDiagnosis, [
        'neuropathy',
        'peripheral neuropathy',
      ]),
      sourceQuestionRefs: ['clientBrainInjuryDiagnosis'],
    },
    {
      questionRef: 'suppReducedKidneyFunction7',
      answer: checkForSpecificConditions(intakeSurvey.clientGenitourinaryDiagnosis, [
        'kidney',
        'renal insufficiency',
        'Chronic kidney disease',
      ]),
      sourceQuestionRefs: ['clientGenitourinaryDiagnosis'],
    },
    {
      questionRef: 'suppRetinopathy7',
      answer: checkForSpecificConditions(intakeSurvey.clientEENTDiagnosis, ['retinopathy']),
      sourceQuestionRefs: ['clientEENTDiagnosis'],
    },
    {
      questionRef: 'suppStroke7',
      answer: checkForSpecificConditions(intakeSurvey.clientBrainInjuryDiagnosis, [
        'stroke',
        'Single stroke',
        'Multiple strokes',
      ]),
      sourceQuestionRefs: ['clientBrainInjuryDiagnosis'],
    },
    {
      questionRef: 'suppTIA7',
      answer: checkForSpecificConditions(intakeSurvey.clientBrainInjuryDiagnosis, [
        'TIA',
        'transient ischemic attack',
      ]),
      sourceQuestionRefs: ['clientBrainInjuryDiagnosis'],
    },
    // Supplement Section - AIDS
    {
      questionRef: 'suppAIDSDiagnosis',
      answer: checkForSpecificConditions(intakeSurvey.clientImmuneSystemDiagnosis, ['AIDS']),
      sourceQuestionRefs: ['clientImmuneSystemDiagnosis'],
    },
    // Supplement Section - Cardiomyopathy
    {
      questionRef: 'suppCardiomyopathy3Years',
      answer: checkForSpecificConditions(intakeSurvey.clientHeartConditionDiagnosis, [
        'cardiomyopathy',
        'Cardiomyopathy or congestive heart failure (CHF)',
      ]),
      sourceQuestionRefs: ['clientHeartConditionDiagnosis'],
    },
    // Supplement Section - Cancer (within timeframes)
    {
      questionRef: 'suppCancer3Years',
      answer:
        checkForSpecificConditions(intakeSurvey.clientDiagnosesList, ['cancer']) === 'yes' ||
        (intakeSurvey.clientCancerTypeLocation && intakeSurvey.clientCancerTypeLocation.length > 0)
          ? 'yes'
          : 'no',
      sourceQuestionRefs: ['clientCancerTypeLocation'],
    },
    {
      questionRef: 'suppBreastCancer10',
      answer: checkForSpecificConditions(intakeSurvey.clientCancerTypeLocation, [
        'breast',
        'Breast Cancer',
      ]),
      sourceQuestionRefs: ['clientCancerTypeLocation'],
    },
    {
      questionRef: 'suppColonCancer10',
      answer: checkForSpecificConditions(intakeSurvey.clientCancerTypeLocation, [
        'colon',
        'colorectal',
      ]),
      sourceQuestionRefs: ['clientCancerTypeLocation'],
    },
    {
      questionRef: 'suppNonMelanomaSkinCancer10',
      answer: checkForSpecificConditions(intakeSurvey.clientCancerTypeLocation, [
        'basal cell',
        'squamous cell',
        'skin cancer',
      ]),
      sourceQuestionRefs: ['clientCancerTypeLocation'],
    },
    {
      questionRef: 'suppProstateCancer10',
      answer: checkForSpecificConditions(intakeSurvey.clientCancerTypeLocation, ['prostate']),
      sourceQuestionRefs: ['clientCancerTypeLocation'],
    },
    {
      questionRef: 'suppStage0or1Melanoma10',
      answer: checkForSpecificConditions(intakeSurvey.clientCancerTypeLocation, ['melanoma'])
        ? 'yes'
        : 'no',
      sourceQuestionRefs: ['clientCancerTypeLocation'],
    },
    {
      questionRef: 'suppThyroidCancer10',
      answer: checkForSpecificConditions(intakeSurvey.clientCancerTypeLocation, ['thyroid']),
      sourceQuestionRefs: ['clientCancerTypeLocation'],
    },
    {
      questionRef: 'suppOtherCancer10',
      answer: checkForSpecificConditions(intakeSurvey.clientCancerTypeLocation, [
        'lung',
        'pancreatic',
        'liver',
        'brain',
        'ovarian',
        'leukemia',
        'lymphoma',
      ]),
      sourceQuestionRefs: ['clientCancerTypeLocation'],
    },
    // Supplement Section - Falls
    {
      questionRef: 'suppBackwardsFall11',
      answer: checkForSpecificConditions(intakeSurvey.clientBrainInjuryDiagnosis, [
        'fall',
        'falls',
      ]),
      sourceQuestionRefs: ['clientBrainInjuryDiagnosis'],
    },
    {
      questionRef: 'suppFracture11',
      answer: checkForSpecificConditions(intakeSurvey.clientInjuriesList, ['fracture']),
      sourceQuestionRefs: ['clientInjuriesList'],
    },
    {
      questionRef: 'suppHeadInjury11',
      answer: checkForSpecificConditions(intakeSurvey.clientBrainInjuryDiagnosis, [
        'head injury',
        'traumatic brain injury',
        'concussion',
      ]),
      sourceQuestionRefs: ['clientBrainInjuryDiagnosis'],
    },
    // Supplement Section - Surgeries
    {
      questionRef: 'suppCarotidArterySurgery12',
      answer: checkForSpecificConditions(intakeSurvey.clientHeartConditionDiagnosis, [
        'carotid surgery',
        'carotid artery surgery',
      ]),
      sourceQuestionRefs: ['clientHeartConditionDiagnosis'],
    },
    {
      questionRef: 'suppHeartSurgery12',
      answer: checkForSpecificConditions(intakeSurvey.clientHeartConditionDiagnosis, [
        'heart surgery',
        'bypass surgery',
        'CABG',
        'valve surgery',
      ]),
      sourceQuestionRefs: ['clientHeartConditionDiagnosis'],
    },
    {
      questionRef: 'suppJointReplacementSurgery12',
      answer: checkForSpecificConditions(intakeSurvey.clientInjuriesList, [
        'joint replacement',
        'hip replacement',
        'knee replacement',
      ]),
      sourceQuestionRefs: ['clientInjuriesList'],
    },
    // Supplement Section - Recent Cardiac Diagnosis
    {
      questionRef: 'suppHeartAttack13',
      answer: checkForSpecificConditions(intakeSurvey.clientHeartConditionDiagnosis, [
        'heart attack',
      ]),
      sourceQuestionRefs: ['clientHeartConditionDiagnosis'],
    },
    {
      questionRef: 'suppStrokeTIA13',
      answer: checkForSpecificConditions(intakeSurvey.clientBrainInjuryDiagnosis, [
        'stroke',
        'TIA',
      ]),
      sourceQuestionRefs: ['clientBrainInjuryDiagnosis'],
    },
  ];

  return mappings;
};

export const getAdvisorUpdatesForLincoln = (
  advisorInfo: AdvisorInfoWithWritingCode,
): PreFillUpdate[] => {
  return [
    {
      questionRef: 'applicationAgentsList',
      answer: [
        {
          firstName: advisorInfo.advisorFirstName,
          lastName: advisorInfo.advisorLastName,
          agentNumber: advisorInfo.bgaWritingCode || '',
        },
      ],
    },
    {
      questionRef: 'primaryCaseContacts',
      answer: [
        {
          contactName: `${advisorInfo.advisorFirstName} ${advisorInfo.advisorLastName}`,
          contactEmail: `${advisorInfo.advisorEmail}`,
          contactPhone:
            advisorInfo.advisorBusinessPhoneNumber || advisorInfo.advisorPhoneNumber || '',
        },
      ],
    },
    {
      questionRef: 'financialProfessionalAltEmail',
      answer: advisorInfo.advisorEmail,
    },
    {
      questionRef: 'agentAffiliatedAgencyName',
      answer: advisorInfo.organizationName ?? '',
    },
    {
      questionRef: 'applicationChecklistAgentName',
      answer: `${advisorInfo.advisorFirstName} ${advisorInfo.advisorLastName}`,
    },
    {
      questionRef: 'disclosureAgentName',
      answer: `${advisorInfo.advisorFirstName} ${advisorInfo.advisorLastName}`,
    },
    {
      questionRef: 'disclosureAgentPhone',
      answer: advisorInfo.advisorBusinessPhoneNumber || advisorInfo.advisorPhoneNumber || '',
    },
    {
      questionRef: 'disclosureAgentAddress',
      answer: GetAdvisorAddress(advisorInfo.advisorBusinessAddress, true),
    },
  ].filter(update => update.answer);
};

export const getPolicyUpdatesForLincoln = (
  policyFundingSource: PolicyFundingSource,
): PreFillUpdate[] => {
  // Prefer updatedQuote if available (has illustration data), otherwise use sourceQuote
  let initialQuote = policyFundingSource.updatedQuote || policyFundingSource.sourceQuote;

  if (typeof initialQuote === 'string') {
    try {
      initialQuote = JSON.parse(initialQuote);
    } catch (e) {
      console.error('Failed to parse quote:', e);
      initialQuote = null;
    }
  }

  // Type guard to check if quote is a PolicyQuote (not Traditional LTC)
  const isPolicyQuote = (quote: any): quote is PolicyQuote => {
    return quote && typeof quote === 'object' && 'premiumAmount' in quote;
  };

  const sourceQuote = isPolicyQuote(initialQuote) ? initialQuote : null;

  return [
    {
      questionRef: 'specifiedAmount',
      answer: returnSafeString(sourceQuote?.deathBenefitInitial),
    },
    {
      questionRef: 'ltcBenefitsRiderDuration',
      answer:
        sourceQuote?.ltcBenefitDurationMonths === 24
          ? '2-years'
          : sourceQuote?.ltcBenefitDurationMonths === 36
            ? '3-years'
            : sourceQuote?.ltcBenefitDurationMonths === 48
              ? '4-years'
              : sourceQuote?.ltcBenefitDurationMonths === 60
                ? '5-years'
                : sourceQuote?.ltcBenefitDurationMonths === 72
                  ? '6-years'
                  : '',
    },
    {
      questionRef: 'otherBenefitsRidersOptions',
      answer: getOtherBenefitsRidersOptions(sourceQuote),
    },
    {
      questionRef: 'premiumAmount',
      answer: returnSafeString(sourceQuote?.premiumAmount),
    },
    {
      questionRef: 'premiumMode',
      answer: getPremiumMode(sourceQuote),
    },
    {
      questionRef: 'inflationProtectionUniversalLife',
      answer: mapInflationOption(sourceQuote).inflationProtectionUniversalLife,
    },
    {
      questionRef: 'inflationProtectionUniversalCompoundPercentage',
      answer: returnSafeString(
        mapInflationOption(sourceQuote).inflationProtectionUniversalCompoundPercentage,
      ),
    },
    {
      questionRef: 'inflationProtectionVariableLife',
      answer:
        sourceQuote?.inflationBenefitOption?.toLowerCase().includes('5') &&
        sourceQuote?.inflationBenefitOption?.toLowerCase().includes('compound')
          ? 'option-b'
          : 'option-a',
    },
    {
      questionRef: 'ltcPremiumType',
      answer: sourceQuote?.productFundingOption?.includes('single')
        ? 'single-premium'
        : sourceQuote?.productFundingOption?.includes('annual') ||
            sourceQuote?.productFundingOption?.includes('10')
          ? 'flexible-premium'
          : '',
    },
    {
      questionRef: 'ltcSinglePremiumAmount',
      answer: returnSafeString(sourceQuote?.premiumAmount),
    },
    {
      questionRef: 'ltcFlexiblePremiumRecurring',
      answer: returnSafeString(sourceQuote?.premiumAmount),
    },
    {
      questionRef: 'ltcBuyInflationProtection',
      answer: mapInflationOption(sourceQuote).inflationProtectionUniversalCompoundPercentage
        ? 'yes'
        : 'no',
    },
    {
      questionRef: 'disclosureFaceAmountCoverageItem',
      answer: 'policy',
    },
    {
      questionRef: 'disclosureTotalInitialAnnualPremium',
      answer: returnSafeString(sourceQuote?.premiumAmount),
    },
    {
      questionRef: 'disclosureTotalPremiumIfUnknown',
      answer: sourceQuote?.productFundingOption?.includes('single')
        ? returnSafeString(sourceQuote?.premiumAmount)
        : sourceQuote?.productFundingOption?.includes('10')
          ? returnSafeString(sourceQuote?.premiumAmount * 10)
          : '',
    },
    {
      questionRef: 'disclosurePlannedPeriodicPremium',
      answer: sourceQuote?.productFundingOption?.includes('10')
        ? returnSafeString(sourceQuote?.premiumAmount)
        : '',
    },
    {
      questionRef: 'disclosureFaceAmountOfPolicy',
      answer: returnSafeString(sourceQuote?.deathBenefitInitial),
    },
  ].filter(update => update.answer);
};

const getCardiacConditions = (intakeSurvey: IntakeSurvey): string => {
  const conditions: string[] = [];
  const heartConditions = intakeSurvey.clientHeartConditionDiagnosis || [];
  const diagnoses = intakeSurvey.clientDiagnosesList || [];

  if (
    checkForSpecificConditions(heartConditions, [
      'aneurysm',
      'Peripheral vascular disease / aneurysm',
    ]) === 'yes'
  ) {
    conditions.push('aneurysm');
  }

  if (
    checkForSpecificConditions(heartConditions, [
      'arrhythmia',
      'palpitations',
      'irregular heartbeat',
      'Atrial fibrillation or other arrhythmia',
    ]) === 'yes'
  ) {
    conditions.push('arrhythmia-palpitations-irregular-heartbeat');
  }

  if (
    checkForSpecificConditions(heartConditions, ['atrial fibrillation', 'afib', 'a-fib']) === 'yes'
  ) {
    conditions.push('atrial-fibrillation');
  }

  if (checkForSpecificConditions(heartConditions, ['bundle branch block', 'BBB']) === 'yes') {
    conditions.push('bundle-branch-block');
  }

  if (checkForSpecificConditions(heartConditions, ['cardioversion', 'ablation']) === 'yes') {
    conditions.push('cardioversion-ablation');
  }

  if (checkForSpecificConditions(heartConditions, ['carotid artery', 'carotid']) === 'yes') {
    conditions.push('carotid-artery-disease');
  }

  if (checkForSpecificConditions(heartConditions, ['chest pain', 'angina']) === 'yes') {
    conditions.push('chest-pain');
  }

  if (
    checkForSpecificConditions(heartConditions, [
      'heart failure',
      'CHF',
      'Congestive Heart Failure',
      'Cardiomyopathy or congestive heart failure (CHF)',
    ]) === 'yes'
  ) {
    conditions.push('heart-failure');
  }

  if (
    checkForSpecificConditions(heartConditions, [
      'heart murmur',
      'murmur',
      'Valvular Heart Disease',
      'Heart valve disease / murmur',
    ]) === 'yes'
  ) {
    conditions.push('heart-murmur');
  }

  if (
    checkForSpecificConditions(heartConditions, [
      'heart valve',
      'valve disorder',
      'Valvular Heart Disease',
      'Heart valve disease',
    ]) === 'yes'
  ) {
    conditions.push('heart-valve-disorder');
  }

  if (
    checkForSpecificConditions(diagnoses, ['high blood pressure', 'hypertension', 'HTN']) === 'yes'
  ) {
    conditions.push('high-blood-pressure');
  }

  if (
    checkForSpecificConditions(heartConditions, ['pacemaker', 'ICD', 'implanted defibrillator']) ===
    'yes'
  ) {
    conditions.push('pacemaker');
  }

  if (
    checkForSpecificConditions(heartConditions, [
      'peripheral vascular disease',
      'peripheral artery disease',
      'PAD',
      'PVD',
      'Peripheral vascular disease / aneurysm',
    ]) === 'yes'
  ) {
    conditions.push('peripheral-vascular-disease');
  }

  if (checkForSpecificConditions(heartConditions, ['venous insufficiency', 'venous']) === 'yes') {
    conditions.push('venous-insufficiency');
  }

  if (checkForSpecificConditions(heartConditions, ['wolff parkinson white', 'WPW']) === 'yes') {
    conditions.push('wolff-parkinson-white-syndrome');
  }

  if (heartConditions.length > 0 && conditions.length === 0) {
    conditions.push('any-other-heart-condition');
  }

  return conditions.join(', ');
};

const getMentalHealthConditions = (intakeSurvey: IntakeSurvey): string => {
  const conditions: string[] = [];
  const mentalHealthIssues = intakeSurvey.clientMentalHealthIssuesList || [];
  const psychiatricDiagnoses = intakeSurvey.clientPsychiatricDiagnosis || [];

  if (
    checkForSpecificConditions(mentalHealthIssues, ['anxiety', 'stress', 'panic']) === 'yes' ||
    checkForSpecificConditions(psychiatricDiagnoses, [
      'Anxiety',
      'panic disorder',
      'panic attacks',
    ]) === 'yes'
  ) {
    conditions.push('anxiety-stress-panic');
  }

  if (
    checkForSpecificConditions(mentalHealthIssues, ['ADD', 'ADHD', 'attention deficit']) ===
      'yes' ||
    checkForSpecificConditions(psychiatricDiagnoses, [
      'Attention deficit hyperactivity disorder (ADHD)',
      'ADHD',
      'ADD',
    ]) === 'yes'
  ) {
    conditions.push('attention-deficit-disorder');
  }

  if (
    checkForSpecificConditions(mentalHealthIssues, ['depression']) === 'yes' ||
    checkForSpecificConditions(psychiatricDiagnoses, ['Depression']) === 'yes'
  ) {
    conditions.push('depression');
  }

  if (
    checkForSpecificConditions(mentalHealthIssues, ['eating disorder', 'anorexia', 'bulimia']) ===
      'yes' ||
    checkForSpecificConditions(psychiatricDiagnoses, ['eating disorder', 'anorexia', 'bulimia']) ===
      'yes'
  ) {
    conditions.push('eating-disorder');
  }

  if (
    checkForSpecificConditions(mentalHealthIssues, ['PTSD', 'post-traumatic']) === 'yes' ||
    checkForSpecificConditions(psychiatricDiagnoses, [
      'Post-traumatic stress disorder (PTSD)',
      'Post-traumatic stress disorder',
      'PTSD',
    ]) === 'yes'
  ) {
    conditions.push('ptsd');
  }

  if (
    checkForSpecificConditions(psychiatricDiagnoses, [
      'Bipolar',
      'Schizophrenia',
      'Borderline personality disorder (BPD)',
      'Obsessive-compulsive disorder (OCD)',
      'OCD',
    ]) === 'yes'
  ) {
    conditions.push('any-other-mental-health');
  }

  if (
    (mentalHealthIssues.length > 0 || psychiatricDiagnoses.length > 0) &&
    conditions.length === 0
  ) {
    conditions.push('any-other-mental-health');
  }

  return conditions.join(', ');
};

const getCancerConditions = (intakeSurvey: IntakeSurvey): string => {
  const conditions: string[] = [];
  const cancerTypes = intakeSurvey.clientCancerTypeLocation || [];
  const diagnoses = intakeSurvey.clientDiagnosesList || [];

  // Abnormal Pap Smear
  if (
    checkForSpecificConditions(cancerTypes, ['abnormal pap', 'pap smear', 'cervical dysplasia']) ===
    'yes'
  ) {
    conditions.push('abnormal-pap-smear');
  }

  // Barrett's Esophagus
  if (checkForSpecificConditions(cancerTypes, ["barrett's esophagus", 'barretts']) === 'yes') {
    conditions.push('barretts-esophagus');
  }

  // Basal Cell/Squamous Cell Cancer of Skin
  if (
    checkForSpecificConditions(cancerTypes, [
      'basal cell',
      'squamous cell',
      'skin cancer',
      'BCC',
      'SCC',
      'Skin',
    ]) === 'yes'
  ) {
    conditions.push('basal-cell-squamous-skin');
  }

  // Bladder Cancer
  if (checkForSpecificConditions(cancerTypes, ['bladder cancer', 'bladder']) === 'yes') {
    conditions.push('bladder-cancer');
  }

  // Breast Cancer
  if (checkForSpecificConditions(cancerTypes, ['breast cancer', 'breast']) === 'yes') {
    conditions.push('breast-cancer');
  }

  // Colon Cancer
  if (
    checkForSpecificConditions(cancerTypes, ['colon cancer', 'colorectal cancer', 'colon']) ===
    'yes'
  ) {
    conditions.push('colon-cancer');
  }

  // Colon Polyps
  if (checkForSpecificConditions(cancerTypes, ['colon polyp', 'polyp']) === 'yes') {
    conditions.push('colon-polyps');
  }

  // Lung Cancer
  if (checkForSpecificConditions(cancerTypes, ['lung cancer', 'lung']) === 'yes') {
    conditions.push('lung-cancer');
  }

  // Lymphoma
  if (checkForSpecificConditions(cancerTypes, ['lymphoma', 'Lymphoma']) === 'yes') {
    conditions.push('lymphoma');
  }

  // Melanoma
  if (checkForSpecificConditions(cancerTypes, ['melanoma']) === 'yes') {
    conditions.push('melanoma');
  }

  // Meningioma
  if (checkForSpecificConditions(cancerTypes, ['meningioma']) === 'yes') {
    conditions.push('meningioma');
  }

  // Polycythemia Vera
  if (checkForSpecificConditions(cancerTypes, ['polycythemia vera', 'polycythemia']) === 'yes') {
    conditions.push('polycythemia-vera');
  }

  // Prostate Cancer
  if (checkForSpecificConditions(cancerTypes, ['prostate cancer', 'prostate']) === 'yes') {
    conditions.push('prostate-cancer');
  }

  // Renal/Kidney Cancer
  if (
    checkForSpecificConditions(cancerTypes, [
      'kidney cancer',
      'renal cancer',
      'kidney',
      'renal',
    ]) === 'yes'
  ) {
    conditions.push('renal-kidney-cancer');
  }

  // Thyroid Cancer
  if (checkForSpecificConditions(cancerTypes, ['thyroid cancer', 'thyroid']) === 'yes') {
    conditions.push('thyroid-cancer');
  }

  // Uterine Cancer
  if (
    checkForSpecificConditions(cancerTypes, ['uterine cancer', 'uterine', 'endometrial']) === 'yes'
  ) {
    conditions.push('uterine-cancer');
  }

  // Any Other Cancer or Precancerous Condition
  if (
    checkForSpecificConditions(diagnoses, ['cancer']) === 'yes' ||
    checkForSpecificConditions(cancerTypes, [
      'tumor',
      'mass',
      'lump',
      'cyst',
      'lesion',
      'Leukemia',
    ]) === 'yes'
  ) {
    if (conditions.length === 0) {
      conditions.push('any-other-cancer');
    }
  }

  return conditions.join(', ');
};

const getMetabolicConditions = (intakeSurvey: IntakeSurvey): string => {
  const conditions: string[] = [];
  const diagnoses = intakeSurvey.clientDiagnosesList || [];
  const eentDiagnoses = intakeSurvey.clientEENTDiagnosis || [];
  const digestiveDiagnoses = intakeSurvey.clientDigestiveDiagnosis || [];

  // Diabetes
  if (checkForSpecificConditions(diagnoses, ['diabetes']) === 'yes') {
    conditions.push('diabetes');
  }

  // Hyperthyroidism/Graves' Disease
  if (
    checkForSpecificConditions(eentDiagnoses, ['hyperthyroidism', "graves' disease", 'graves']) ===
    'yes'
  ) {
    conditions.push('hyperthyroidism-graves');
  }

  // Parathyroid Disorder/Disease
  if (checkForSpecificConditions(eentDiagnoses, ['parathyroid']) === 'yes') {
    conditions.push('parathyroid-disorder');
  }

  // Pituitary Adenoma/Tumor
  if (checkForSpecificConditions(eentDiagnoses, ['pituitary']) === 'yes') {
    conditions.push('pituitary-adenoma');
  }

  // Thyroid Nodules
  if (checkForSpecificConditions(eentDiagnoses, ['thyroid nodule', 'thyroid nodules']) === 'yes') {
    conditions.push('thyroid-nodules');
  }

  // Any Other Metabolic or Endocrine Condition
  if (
    checkForSpecificConditions(diagnoses, ['metabolic', 'endocrine']) === 'yes' ||
    checkForSpecificConditions(digestiveDiagnoses, ['metabolic', 'endocrine']) === 'yes'
  ) {
    if (conditions.length === 0) {
      conditions.push('any-other-metabolic');
    }
  }

  return conditions.join(', ');
};

const getRespiratoryConditions = (intakeSurvey: IntakeSurvey): string => {
  const conditions: string[] = [];
  const lungDiagnoses = intakeSurvey.clientLungRespiratoryDiagnosis || [];
  const diagnoses = intakeSurvey.clientDiagnosesList || [];

  // Asthma/Reactive Airway Disease
  if (
    checkForSpecificConditions(lungDiagnoses, ['asthma', 'Asthma', 'reactive airway']) === 'yes' ||
    checkForSpecificConditions(diagnoses, ['asthma']) === 'yes'
  ) {
    conditions.push('asthma-reactive-airway');
  }

  // Bronchiectasis
  if (checkForSpecificConditions(lungDiagnoses, ['bronchiectasis']) === 'yes') {
    conditions.push('bronchiectasis');
  }

  // Emphysema/COPD/Chronic Bronchitis
  if (
    checkForSpecificConditions(lungDiagnoses, [
      'emphysema',
      'Emphysema',
      'COPD',
      'COPD / emphysema',
      'chronic obstructive pulmonary disease',
      'chronic bronchitis',
      'Chronic bronchitis',
    ]) === 'yes' ||
    checkForSpecificConditions(diagnoses, ['COPD', 'emphysema']) === 'yes'
  ) {
    conditions.push('emphysema-copd');
  }

  // Pulmonary Fibrosis
  if (checkForSpecificConditions(lungDiagnoses, ['pulmonary fibrosis', 'fibrosis']) === 'yes') {
    conditions.push('pulmonary-fibrosis');
  }

  // Pulmonary Nodules
  if (checkForSpecificConditions(lungDiagnoses, ['pulmonary nodule', 'lung nodule']) === 'yes') {
    conditions.push('pulmonary-nodules');
  }

  // Sarcoidosis
  if (checkForSpecificConditions(lungDiagnoses, ['sarcoidosis']) === 'yes') {
    conditions.push('sarcoidosis');
  }

  // Sleep Apnea
  if (
    checkForSpecificConditions(lungDiagnoses, ['sleep apnea', 'Sleep apnea']) === 'yes' ||
    checkForSpecificConditions(diagnoses, ['sleep apnea']) === 'yes'
  ) {
    conditions.push('sleep-apnea');
  }

  // Tuberculosis
  if (
    checkForSpecificConditions(lungDiagnoses, ['tuberculosis', 'TB']) === 'yes' ||
    checkForSpecificConditions(diagnoses, ['tuberculosis']) === 'yes'
  ) {
    conditions.push('tuberculosis');
  }

  // Any Other Lung or Respiratory Condition
  if (lungDiagnoses.length > 0 && conditions.length === 0) {
    conditions.push('any-other-lung');
  }

  return conditions.join(', ');
};

const getGastrointestinalConditions = (intakeSurvey: IntakeSurvey): string => {
  const conditions: string[] = [];
  const digestiveDiagnoses = intakeSurvey.clientDigestiveDiagnosis || [];
  const diagnoses = intakeSurvey.clientDiagnosesList || [];

  // Bowel Incontinence
  if (
    checkForSpecificConditions(digestiveDiagnoses, ['bowel incontinence', 'fecal incontinence']) ===
    'yes'
  ) {
    conditions.push('bowel-incontinence');
  }

  // Bowel Obstruction
  if (
    checkForSpecificConditions(digestiveDiagnoses, [
      'bowel obstruction',
      'intestinal obstruction',
    ]) === 'yes'
  ) {
    conditions.push('bowel-obstruction');
  }

  // Celiac Disease
  if (
    checkForSpecificConditions(digestiveDiagnoses, ['celiac disease', 'celiac']) === 'yes' ||
    checkForSpecificConditions(diagnoses, ['celiac']) === 'yes'
  ) {
    conditions.push('celiac-disease');
  }

  // Crohn's Disease
  if (
    checkForSpecificConditions(digestiveDiagnoses, [
      "Crohn's disease",
      "crohn's",
      "Ulcerative Colitis / Crohn's Disease (IBD)",
    ]) === 'yes' ||
    checkForSpecificConditions(diagnoses, ["crohn's"]) === 'yes'
  ) {
    conditions.push('crohns-disease');
  }

  // Diverticulitis
  if (
    checkForSpecificConditions(digestiveDiagnoses, ['diverticulitis', 'diverticulosis']) === 'yes'
  ) {
    conditions.push('diverticulitis');
  }

  // Esophagitis
  if (checkForSpecificConditions(digestiveDiagnoses, ['esophagitis']) === 'yes') {
    conditions.push('esophagitis');
  }

  // Esophageal Stricture
  if (
    checkForSpecificConditions(digestiveDiagnoses, ['esophageal stricture', 'stricture']) === 'yes'
  ) {
    conditions.push('esophageal-stricture');
  }

  // Esophageal Varices
  if (checkForSpecificConditions(digestiveDiagnoses, ['esophageal varices', 'varices']) === 'yes') {
    conditions.push('esophageal-varices');
  }

  // Fatty Liver
  if (
    checkForSpecificConditions(digestiveDiagnoses, ['fatty liver', 'hepatic steatosis']) ===
      'yes' ||
    checkForSpecificConditions(diagnoses, ['fatty liver']) === 'yes'
  ) {
    conditions.push('fatty-liver');
  }

  // Gallstones
  if (
    checkForSpecificConditions(digestiveDiagnoses, [
      'gallstone',
      'gallstones',
      'cholelithiasis',
    ]) === 'yes'
  ) {
    conditions.push('gallstones');
  }

  // Gastric Bypass
  if (
    checkForSpecificConditions(digestiveDiagnoses, ['gastric bypass', 'bariatric surgery']) ===
    'yes'
  ) {
    conditions.push('gastric-bypass');
  }

  // GERD
  if (
    checkForSpecificConditions(digestiveDiagnoses, [
      'GERD',
      'gastroesophageal reflux',
      'acid reflux',
      'reflux',
    ]) === 'yes' ||
    checkForSpecificConditions(diagnoses, ['GERD']) === 'yes'
  ) {
    conditions.push('gerd');
  }

  // Gastrointestinal Bleed
  if (
    checkForSpecificConditions(digestiveDiagnoses, [
      'gastrointestinal bleed',
      'GI bleed',
      'bleeding',
    ]) === 'yes'
  ) {
    conditions.push('gastrointestinal-bleed');
  }

  // Gilbert's Disease
  if (checkForSpecificConditions(digestiveDiagnoses, ["gilbert's disease", 'gilbert']) === 'yes') {
    conditions.push('gilberts-disease');
  }

  // Hepatitis
  if (
    checkForSpecificConditions(digestiveDiagnoses, ['hepatitis', 'Hepatitis A / B / C']) ===
      'yes' ||
    checkForSpecificConditions(diagnoses, ['hepatitis']) === 'yes'
  ) {
    conditions.push('hepatitis');
  }

  // Irritable Bowel Syndrome (IBS)
  if (
    checkForSpecificConditions(digestiveDiagnoses, [
      'irritable bowel syndrome',
      'IBS',
      'Irritable bowel syndrome (IBS)',
      'Irritable bowl syndrome (IBS)',
    ]) === 'yes'
  ) {
    conditions.push('irritable-bowel-syndrome');
  }

  // Pancreatitis
  if (
    checkForSpecificConditions(digestiveDiagnoses, ['pancreatitis']) === 'yes' ||
    checkForSpecificConditions(diagnoses, ['pancreatitis']) === 'yes'
  ) {
    conditions.push('pancreatitis');
  }

  // Ulcers
  if (
    checkForSpecificConditions(digestiveDiagnoses, ['ulcer', 'peptic ulcer', 'gastric ulcer']) ===
    'yes'
  ) {
    conditions.push('ulcers');
  }

  // Ulcerative Colitis/Colitis
  if (
    checkForSpecificConditions(digestiveDiagnoses, [
      'ulcerative colitis',
      'colitis',
      "Ulcerative Colitis / Crohn's Disease (IBD)",
    ]) === 'yes'
  ) {
    conditions.push('ulcerative-colitis');
  }

  // Any Other Gastrointestinal Condition
  if (digestiveDiagnoses.length > 0 && conditions.length === 0) {
    conditions.push('any-other-gastrointestinal');
  }

  return conditions.join(', ');
};

const getUrinaryConditions = (intakeSurvey: IntakeSurvey): string => {
  const conditions: string[] = [];
  const genitourinaryDiagnoses = intakeSurvey.clientGenitourinaryDiagnosis || [];
  const diagnoses = intakeSurvey.clientDiagnosesList || [];

  // Bladder Incontinence
  if (
    checkForSpecificConditions(genitourinaryDiagnoses, [
      'bladder incontinence',
      'urinary incontinence',
      'Bladder issues',
    ]) === 'yes' ||
    checkForSpecificConditions(diagnoses, ['bladder incontinence', 'urinary incontinence']) ===
      'yes'
  ) {
    conditions.push('bladder-incontinence');
  }

  // Chronic Renal Insufficiency
  if (
    checkForSpecificConditions(genitourinaryDiagnoses, [
      'chronic renal insufficiency',
      'renal insufficiency',
      'Chronic kidney disease',
    ]) === 'yes' ||
    checkForSpecificConditions(diagnoses, ['renal insufficiency']) === 'yes'
  ) {
    conditions.push('chronic-renal-insufficiency');
  }

  // Glomerulonephritis
  if (checkForSpecificConditions(genitourinaryDiagnoses, ['glomerulonephritis']) === 'yes') {
    conditions.push('glomerulonephritis');
  }

  // Interstitial Cystitis
  if (
    checkForSpecificConditions(genitourinaryDiagnoses, ['interstitial cystitis', 'cystitis']) ===
    'yes'
  ) {
    conditions.push('interstitial-cystitis');
  }

  // Kidney Stones
  if (
    checkForSpecificConditions(genitourinaryDiagnoses, [
      'kidney stone',
      'kidney stones',
      'renal calculi',
    ]) === 'yes' ||
    checkForSpecificConditions(diagnoses, ['kidney stone']) === 'yes'
  ) {
    conditions.push('kidney-stones');
  }

  // Nephritis
  if (checkForSpecificConditions(genitourinaryDiagnoses, ['nephritis']) === 'yes') {
    conditions.push('nephritis');
  }

  // Nephrotic Syndrome
  if (checkForSpecificConditions(genitourinaryDiagnoses, ['nephrotic syndrome']) === 'yes') {
    conditions.push('nephrotic-syndrome');
  }

  // Overactive Bladder
  if (
    checkForSpecificConditions(genitourinaryDiagnoses, ['overactive bladder', 'OAB']) === 'yes' ||
    checkForSpecificConditions(diagnoses, ['overactive bladder']) === 'yes'
  ) {
    conditions.push('overactive-bladder');
  }

  // Polycystic Kidney Disease
  if (
    checkForSpecificConditions(genitourinaryDiagnoses, [
      'polycystic kidney disease',
      'polycystic kidney',
      'PKD',
    ]) === 'yes'
  ) {
    conditions.push('polycystic-kidney-disease');
  }

  // Pyelonephritis
  if (checkForSpecificConditions(genitourinaryDiagnoses, ['pyelonephritis']) === 'yes') {
    conditions.push('pyelonephritis');
  }

  // Renal Cyst
  if (checkForSpecificConditions(genitourinaryDiagnoses, ['renal cyst', 'kidney cyst']) === 'yes') {
    conditions.push('renal-cyst');
  }

  // Any Other Urinary Condition
  if (genitourinaryDiagnoses.length > 0 && conditions.length === 0) {
    conditions.push('any-other-urinary');
  }

  return conditions.join(', ');
};

const getBloodDisorders = (intakeSurvey: IntakeSurvey): string => {
  const conditions: string[] = [];
  const diagnoses = intakeSurvey.clientDiagnosesList || [];

  // Anemia
  if (checkForSpecificConditions(diagnoses, ['anemia']) === 'yes') {
    conditions.push('anemia');
  }

  // Any Other Blood Disorder
  if (
    checkForSpecificConditions(diagnoses, ['blood disorder']) === 'yes' &&
    conditions.length === 0
  ) {
    conditions.push('any-other-blood');
  }

  return conditions.join(', ');
};

const getBrainNervousConditions = (intakeSurvey: IntakeSurvey): string => {
  const conditions: string[] = [];
  const brainDiagnoses = intakeSurvey.clientBrainInjuryDiagnosis || [];
  const diagnoses = intakeSurvey.clientDiagnosesList || [];

  // Acoustic Neuroma
  if (checkForSpecificConditions(brainDiagnoses, ['acoustic neuroma']) === 'yes') {
    conditions.push('acoustic-neuroma');
  }

  // Balance Disorder
  if (
    checkForSpecificConditions(brainDiagnoses, ['balance disorder', 'balance problem']) === 'yes'
  ) {
    conditions.push('balance-disorder');
  }

  // Concussion/Post Concussive Syndrome
  if (
    checkForSpecificConditions(brainDiagnoses, ['concussion', 'post concussive syndrome']) ===
      'yes' ||
    checkForSpecificConditions(diagnoses, ['concussion']) === 'yes'
  ) {
    conditions.push('concussion');
  }

  // Falls
  if (checkForSpecificConditions(brainDiagnoses, ['falls', 'fall']) === 'yes') {
    conditions.push('falls');
  }

  // Headaches/Migraines
  if (
    checkForSpecificConditions(brainDiagnoses, ['headache', 'migraine']) === 'yes' ||
    checkForSpecificConditions(diagnoses, ['migraine', 'headache']) === 'yes'
  ) {
    conditions.push('headaches-migraines');
  }

  // Paralysis/Hemiplegia/Paraplegia
  if (
    checkForSpecificConditions(brainDiagnoses, ['paralysis', 'hemiplegia', 'paraplegia']) ===
      'yes' ||
    checkForSpecificConditions(diagnoses, ['paralysis']) === 'yes'
  ) {
    conditions.push('paralysis-hemiplegia');
  }

  // Peripheral Neuropathy
  if (
    checkForSpecificConditions(brainDiagnoses, ['peripheral neuropathy', 'neuropathy']) === 'yes' ||
    checkForSpecificConditions(diagnoses, ['neuropathy']) === 'yes'
  ) {
    conditions.push('peripheral-neuropathy');
  }

  // Restless Leg Syndrome
  if (
    checkForSpecificConditions(brainDiagnoses, [
      'restless leg syndrome',
      'restless legs',
      'RLS',
    ]) === 'yes'
  ) {
    conditions.push('restless-leg-syndrome');
  }

  // Seizures/Epilepsy
  if (
    checkForSpecificConditions(brainDiagnoses, ['seizure', 'epilepsy', 'Epilepsy / seizures']) ===
      'yes' ||
    checkForSpecificConditions(diagnoses, ['seizure', 'epilepsy']) === 'yes'
  ) {
    conditions.push('seizures-epilepsy');
  }

  // Stroke/TIA
  if (
    checkForSpecificConditions(brainDiagnoses, [
      'stroke',
      'Single stroke',
      'Multiple strokes',
      'TIA',
      'transient ischemic attack',
      'TIA ("mini stroke")',
    ]) === 'yes' ||
    checkForSpecificConditions(diagnoses, ['stroke', 'TIA']) === 'yes'
  ) {
    conditions.push('stroke-tia');
  }

  // Syncope/Fainting
  if (
    checkForSpecificConditions(brainDiagnoses, ['syncope', 'fainting']) === 'yes' ||
    checkForSpecificConditions(diagnoses, ['syncope', 'fainting']) === 'yes'
  ) {
    conditions.push('syncope');
  }

  // Transient Global Amnesia
  if (
    checkForSpecificConditions(brainDiagnoses, ['transient global amnesia', 'global amnesia']) ===
    'yes'
  ) {
    conditions.push('global-amnesia');
  }

  // Traumatic Brain Injury
  if (
    checkForSpecificConditions(brainDiagnoses, ['traumatic brain injury', 'TBI']) === 'yes' ||
    checkForSpecificConditions(diagnoses, ['traumatic brain injury', 'TBI']) === 'yes'
  ) {
    conditions.push('tbi');
  }

  // Tremors
  if (
    checkForSpecificConditions(brainDiagnoses, ['tremor', 'tremors', 'Tremors', 'Parkinson']) ===
      'yes' ||
    checkForSpecificConditions(diagnoses, ['tremor']) === 'yes'
  ) {
    conditions.push('tremor');
  }

  // Vertigo/Dizziness
  if (
    checkForSpecificConditions(brainDiagnoses, ['vertigo', 'dizziness']) === 'yes' ||
    checkForSpecificConditions(diagnoses, ['vertigo', 'dizziness']) === 'yes'
  ) {
    conditions.push('vertigo');
  }

  // Any Other Brain or Nervous System Disorder
  if (brainDiagnoses.length > 0 && conditions.length === 0) {
    conditions.push('any-other-brain');
  }

  return conditions.join(', ');
};

const getMusculoskeletalConditions = (intakeSurvey: IntakeSurvey): string => {
  const conditions: string[] = [];
  const diagnoses = intakeSurvey.clientDiagnosesList || [];
  const injuries = intakeSurvey.clientInjuriesList || [];

  // Adhesive Capsulitis/Frozen Shoulder
  if (checkForSpecificConditions(injuries, ['adhesive capsulitis', 'frozen shoulder']) === 'yes') {
    conditions.push('adhesive-capsulitis');
  }

  // Amputation
  if (checkForSpecificConditions(injuries, ['amputation']) === 'yes') {
    conditions.push('amputation');
  }

  // Bone Spur
  if (checkForSpecificConditions(injuries, ['bone spur']) === 'yes') {
    conditions.push('bone-spur');
  }

  // Bunion
  if (checkForSpecificConditions(injuries, ['bunion']) === 'yes') {
    conditions.push('bunion');
  }

  // Bursitis
  if (checkForSpecificConditions(injuries, ['bursitis']) === 'yes') {
    conditions.push('bursitis');
  }

  // Degenerative Disc Disease
  if (
    checkForSpecificConditions(injuries, ['degenerative disc disease', 'degenerative disc']) ===
    'yes'
  ) {
    conditions.push('degenerative-disc-disease');
  }

  // Fracture
  if (checkForSpecificConditions(injuries, ['fracture', 'broken bone']) === 'yes') {
    conditions.push('fracture');
  }

  // Gout
  if (checkForSpecificConditions(diagnoses, ['gout']) === 'yes') {
    conditions.push('gout');
  }

  // Herniated Disc/Bulging Disc
  if (
    checkForSpecificConditions(injuries, ['herniated disc', 'bulging disc', 'slipped disc']) ===
    'yes'
  ) {
    conditions.push('herniated-disc');
  }

  // Joint/Spinal Injections
  if (checkForSpecificConditions(injuries, ['joint injection', 'spinal injection']) === 'yes') {
    conditions.push('joint-spinal-injections');
  }

  // Meniscus Injury
  if (
    checkForSpecificConditions(injuries, [
      'meniscus injury',
      'meniscus tear',
      'meniscus surgery',
    ]) === 'yes'
  ) {
    conditions.push('meniscus-injury');
  }

  // Osteopenia
  if (checkForSpecificConditions(diagnoses, ['osteopenia']) === 'yes') {
    conditions.push('osteopenia');
  }

  // Osteoporosis
  if (checkForSpecificConditions(diagnoses, ['osteoporosis']) === 'yes') {
    conditions.push('osteoporosis');
  }

  // Radiculopathy
  if (checkForSpecificConditions(injuries, ['radiculopathy']) === 'yes') {
    conditions.push('radiculopathy');
  }

  // Sciatica
  if (
    checkForSpecificConditions(injuries, ['sciatica']) === 'yes' ||
    checkForSpecificConditions(diagnoses, ['sciatica']) === 'yes'
  ) {
    conditions.push('sciatica');
  }

  // Scoliosis/Kyphosis
  if (checkForSpecificConditions(injuries, ['scoliosis', 'kyphosis']) === 'yes') {
    conditions.push('scoliosis-kyphosis');
  }

  // Spinal Stenosis
  if (checkForSpecificConditions(injuries, ['spinal stenosis']) === 'yes') {
    conditions.push('spinal-stenosis');
  }

  // Spondylosis/Spondylolisthesis/Spondylitis
  if (
    checkForSpecificConditions(injuries, ['spondylosis', 'spondylolisthesis', 'spondylitis']) ===
    'yes'
  ) {
    conditions.push('spondylosis');
  }

  // Sprain/Strain
  if (checkForSpecificConditions(injuries, ['sprain', 'strain']) === 'yes') {
    conditions.push('sprain-strain');
  }

  // Tendinitis
  if (checkForSpecificConditions(injuries, ['tendinitis', 'tendonitis']) === 'yes') {
    conditions.push('tendinitis');
  }

  // Trigger Finger/Dupuytren's Contracture
  if (
    checkForSpecificConditions(injuries, ['trigger finger', "dupuytren's contracture"]) === 'yes'
  ) {
    conditions.push('trigger-finger');
  }

  // Any Other Musculoskeletal
  if (
    (checkForSpecificConditions(diagnoses, ['musculoskeletal']) === 'yes' || injuries.length > 0) &&
    conditions.length === 0
  ) {
    conditions.push('any-other-musculoskeletal');
  }

  return conditions.join(', ');
};

const getOtherBenefitsRidersOptions = (quote: PolicyQuote | null): string => {
  const lines: string[] = [];
  if (!quote) return '';

  if (quote.inflationBenefitOption) {
    lines.push(`Inflation Benefit: ${quote.inflationBenefitOption}`);
  }
  if (quote.returnOfPremiumPercent != null) {
    lines.push(`Return of Premium: ${quote.returnOfPremiumPercent}%`);
  }
  if (quote.productFundingOption) {
    lines.push(`Funding Option: ${quote.productFundingOption}`);
  }
  if (quote.cobPaymentOption) {
    lines.push(`COB Payment Option: ${quote.cobPaymentOption}`);
  }
  if (quote.hasGlobalBenefits === true) {
    lines.push(`Global Benefits: Included`);
  }
  if (lines.length === 0) return '';

  return lines.join(' / ');
};

const getPremiumMode = (quote: PolicyQuote | null): string => {
  if (!quote) return '';
  const source =
    quote.productFundingOption?.toLowerCase() || quote.cobPaymentOption?.toLowerCase() || '';
  if (source.includes('single')) return 'single-premium';
  if (source.includes('annual') || source.includes('10')) return 'annual';
  if (source.includes('semi')) return 'semi-annual';
  if (source.includes('quarter')) return 'quarterly';
  if (source.includes('month')) return 'monthly';
  return 'other';
};

const mapInflationOption = (quote: PolicyQuote | null) => {
  if (!quote || !quote.inflationBenefitOption) {
    return {
      inflationProtectionUniversalLife: 'option-a',
      inflationProtectionUniversalCompoundPercentage: null,
    };
  }
  const raw = quote.inflationBenefitOption?.toLowerCase() || '';

  if (raw.includes('no') || raw.includes('none') || raw.includes('zero')) {
    return {
      inflationProtectionUniversalLife: 'option-a',
      inflationProtectionUniversalCompoundPercentage: null,
    };
  }

  // Extract percentage (if any)
  const match = raw.match(/(\d+(\.\d+)?)/);
  const percent = match ? parseFloat(match[1]) : null;

  // OPTION C — Exactly 5% compound
  if (raw.includes('5') && raw.includes('compound')) {
    return {
      inflationProtectionUniversalLife: 'option-c',
      inflationProtectionUniversalCompoundPercentage: null,
    };
  }

  // OPTION B — Any other compound inflation
  if (raw.includes('compound') && percent !== null && percent !== 5) {
    return {
      inflationProtectionUniversalLife: 'option-b',
      inflationProtectionUniversalCompoundPercentage: percent,
    };
  }

  return {
    inflationProtectionUniversalLife: 'option-a',
    inflationProtectionUniversalCompoundPercentage: null,
  };
};
